import React, { useCallback, useContext, useMemo, useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  CircularProgress,
  Button,
  MenuItem,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import { toast } from "react-toastify";

import api from "../../services/api";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TicketModal from "../../components/TicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  filterControl: {
    minWidth: 180,
  },
  tagsFilterControl: {
    minWidth: 240,
  },
  tableContainer: {
    maxHeight: "calc(100vh - 230px)",
  },
  actionsCell: {
    width: 120,
    whiteSpace: "nowrap",
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
}));

const ALLOWED_STATUSES = ["closed", "concluido", "finalizado"];

const getTodayDateInputValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatFinishedAt = (finishedAt) => {
  if (!finishedAt) {
    return "-";
  }

  const parsedDate = new Date(finishedAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(parsedDate);
};

const TicketsConference = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [todayDate] = useState(() => getTodayDateInputValue());

  const [tickets, setTickets] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [startDate, setStartDate] = useState(todayDate);
  const [endDate, setEndDate] = useState(todayDate);
  const [selectedQueueId, setSelectedQueueId] = useState("");
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedContactTagIds, setSelectedContactTagIds] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketUuid, setTicketUuid] = useState(null);

  const userQueueIds = useMemo(
    () => user?.queues?.map((q) => q.id) || [],
    [user?.queues]
  );

  const selectedQueueIds = useMemo(() => {
    if (selectedQueueId) {
      return [Number(selectedQueueId)];
    }

    return userQueueIds;
  }, [selectedQueueId, userQueueIds]);

  const selectedWhatsappIds = useMemo(() => {
    if (!selectedWhatsappId) {
      return [];
    }

    return [Number(selectedWhatsappId)];
  }, [selectedWhatsappId]);

  const selectedCampaignIds = useMemo(() => {
    if (!selectedCampaignId) {
      return [];
    }

    return [Number(selectedCampaignId)];
  }, [selectedCampaignId]);

  const selectedUserIds = useMemo(() => {
    if (!selectedUserId) {
      return [];
    }

    return [Number(selectedUserId)];
  }, [selectedUserId]);

  useEffect(() => {
    let isMounted = true;

    const fetchFilterOptions = async () => {
      try {
        const [{ data: whatsappsData }, { data: campaignsData }, { data: usersData }] = await Promise.all([
          api.get("/whatsapp"),
          api.get("/campaigns/", {
            params: {
              pageNumber: 1,
              searchParam: "",
            },
          }),
          api.get("/users"),
        ]);

        if (!isMounted) {
          return;
        }

        setWhatsapps(Array.isArray(whatsappsData) ? whatsappsData : []);
        setCampaigns(Array.isArray(campaignsData?.records) ? campaignsData.records : []);
        setUsers(Array.isArray(usersData?.users) ? usersData.users : []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchFilterOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  // Data: 28/07/2026 | Motivo: aqui havia um early-return quando selectedQueueIds
  // estava vazio — a requisicao nem saia do navegador. Empresa sem nenhuma fila
  // cadastrada tem todos os tickets com queueId NULL e usuario com userQueueIds
  // vazio, entao a tela ficava sempre em "Nenhum ticket finalizado encontrado".
  // O recorte de fila/allTicket/conexao e do backend (buildTicketScopeWhere +
  // CanAccessTicket) — o frontend nunca corta a lista por conta propria, igual
  // TicketsList/TicketsListCustom, que mandam queueIds mesmo vazio.
  const fetchTickets = useCallback(async ({ resetPage = false, targetPage = 1 } = {}) => {
    setLoading(true);

    try {
      const { data } = await api.get("/tickets", {
        params: {
          pageNumber: targetPage,
          searchParam,
          searchBy: "contact",
          startDate,
          endDate,
          status: ALLOWED_STATUSES.join(","),
          showAll: true,
          queueIds: JSON.stringify(selectedQueueIds),
          whatsappIds: JSON.stringify(selectedWhatsappIds),
          campaignIds: JSON.stringify(selectedCampaignIds),
          users: JSON.stringify(selectedUserIds),
          contactTags: JSON.stringify(selectedContactTagIds),
        },
      });

      const fetchedTickets = Array.isArray(data?.tickets) ? data.tickets : [];

      setTickets((prev) => (resetPage ? fetchedTickets : [...prev, ...fetchedTickets]));
      setHasMore(Boolean(data?.hasMore));
      setPageNumber(targetPage + 1);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [
    searchParam,
    selectedQueueIds,
    selectedWhatsappIds,
    selectedCampaignIds,
    selectedUserIds,
    selectedContactTagIds,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    setPageNumber(1);
    fetchTickets({ resetPage: true, targetPage: 1 });
  }, [
    searchParam,
    startDate,
    endDate,
    selectedQueueId,
    selectedWhatsappId,
    selectedCampaignId,
    selectedUserId,
    selectedContactTagIds,
    fetchTickets,
  ]);

  const handleClearFilters = () => {
    setSearchParam("");
    setStartDate(todayDate);
    setEndDate(todayDate);
    setSelectedQueueId("");
    setSelectedWhatsappId("");
    setSelectedCampaignId("");
    setSelectedUserId("");
    setSelectedContactTagIds([]);
    setPageNumber(1);
  };

  const handleSelectedContactTags = useCallback((selecteds) => {
    setSelectedContactTagIds(selecteds.map((tag) => tag.id));
  }, []);

  const handleToggleConferenceCheck = async (ticketId, checked) => {
    try {
      await api.put(`/tickets/${ticketId}`, {
        isConferenceChecked: checked,
      });

      setTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket.id === ticketId
            ? { ...ticket, isConferenceChecked: checked }
            : ticket
        )
      );

      toast.success(i18n.t("ticketsConference.toasts.updated"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenTicketChat = (ticket) => {
    if (!ticket?.uuid) {
      toast.error(i18n.t("ticketsConference.toasts.openChatError"));
      return;
    }

    setTicketUuid(ticket.uuid);
    setTicketModalOpen(true);
  };

  return (
    <div className={classes.root}>
      <TicketModal
        open={ticketModalOpen}
        onClose={() => {
          setTicketModalOpen(false);
          setTicketUuid(null);
        }}
        ticketId={ticketUuid}
      />

      <MainHeader>
        <Title>{i18n.t("ticketsConference.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("ticketsConference.subtitle")}
          </Typography>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper variant="outlined" style={{ padding: 12 }}>
        <div className={classes.filterRow}>
          <TextField
            variant="outlined"
            size="small"
            label={i18n.t("ticketsConference.filters.search")}
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            style={{ minWidth: 260, flex: 1 }}
          />
          <TextField
            select
            variant="outlined"
            size="small"
            label={i18n.t("ticketsConference.filters.queue")}
            value={selectedQueueId}
            onChange={(e) => setSelectedQueueId(e.target.value)}
            className={classes.filterControl}
          >
            <MenuItem value="">{i18n.t("ticketsConference.filters.allQueues")}</MenuItem>
            {(user?.queues || []).map((queue) => (
              <MenuItem key={queue.id} value={String(queue.id)}>
                {queue.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            variant="outlined"
            size="small"
            label="WhatsApp"
            value={selectedWhatsappId}
            onChange={(e) => setSelectedWhatsappId(e.target.value)}
            className={classes.filterControl}
          >
            <MenuItem value="">{i18n.t("common.all")}</MenuItem>
            {whatsapps.map((whatsapp) => (
              <MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
                {whatsapp.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            variant="outlined"
            size="small"
            label={i18n.t("ticketsConference.filters.user")}
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className={classes.filterControl}
          >
            <MenuItem value="">{i18n.t("common.all")}</MenuItem>
            {users.map((responsibleUser) => (
              <MenuItem key={responsibleUser.id} value={String(responsibleUser.id)}>
                {responsibleUser.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            variant="outlined"
            size="small"
            label={i18n.t("ticketsConference.filters.campaign")}
            value={selectedCampaignId}
            onChange={(e) => setSelectedCampaignId(e.target.value)}
            className={classes.filterControl}
          >
            <MenuItem value="">{i18n.t("ticketsConference.filters.allCampaigns")}</MenuItem>
            {campaigns.map((campaign) => (
              <MenuItem key={campaign.id} value={String(campaign.id)}>
                {campaign.name}
              </MenuItem>
            ))}
          </TextField>
          <div className={classes.tagsFilterControl}>
            <TagsFilter
              compact
              label={i18n.t("ticketsList.filters.contactTags")}
              placeholder={i18n.t("ticketsConference.filters.allTags")}
              selectedIds={selectedContactTagIds}
              onFiltered={handleSelectedContactTags}
            />
          </div>
          <TextField
            variant="outlined"
            size="small"
            label={i18n.t("common.startDate")}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            variant="outlined"
            size="small"
            label={i18n.t("common.endDate")}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" onClick={handleClearFilters}>
            {i18n.t("common.clear")}
          </Button>
        </div>
      </Paper>

      <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>{i18n.t("ticketsConference.table.contact")}</TableCell>
              <TableCell>{i18n.t("ticketsConference.table.queue")}</TableCell>
              <TableCell>{i18n.t("ticketsConference.table.user")}</TableCell>
              <TableCell>{i18n.t("common.status")}</TableCell>
              <TableCell>{i18n.t("ticketsConference.table.finishedAt")}</TableCell>
              <TableCell className={classes.actionsCell}>{i18n.t("ticketsConference.table.checked")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.map((ticket) => (
              <TableRow key={ticket.id} hover>
                <TableCell>{ticket.id}</TableCell>
                <TableCell>{ticket?.contact?.name || "-"}</TableCell>
                <TableCell>{ticket?.queue?.name || i18n.t("ticketsConference.table.noQueue")}</TableCell>
                <TableCell>{ticket?.user?.name || "-"}</TableCell>
                <TableCell>{ticket?.status || "-"}</TableCell>
                <TableCell>{formatFinishedAt(ticket?.finishedAt)}</TableCell>
                <TableCell className={classes.actionsCell}>
                  <div className={classes.actionButtons}>
                    <Tooltip title={i18n.t("ticketsConference.actions.openChat")}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenTicketChat(ticket)}
                      >
                        <WhatsAppIcon fontSize="small" style={{ color: "#25d366" }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip
                      title={ticket?.isConferenceChecked ? i18n.t("ticketsConference.actions.uncheck") : i18n.t("ticketsConference.actions.check")}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleToggleConferenceCheck(ticket.id, !Boolean(ticket?.isConferenceChecked))
                        }
                      >
                        {ticket?.isConferenceChecked ? (
                          <CheckCircleIcon fontSize="small" color="primary" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" color="action" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}

            {!loading && tickets.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {i18n.t("ticketsConference.table.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper variant="outlined" className={classes.footer}>
        {loading ? (
          <CircularProgress size={26} />
        ) : hasMore ? (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => fetchTickets({ resetPage: false, targetPage: pageNumber })}
          >
            {i18n.t("common.loadMore")}
          </Button>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {i18n.t("ticketsConference.table.allLoaded")}
          </Typography>
        )}
      </Paper>
    </div>
  );
};

export default TicketsConference;
